import type { MusicCue } from '@/utils/arcade/musicAnalysis';
// The note chart shared by the rhythm games. Every grid tick schedules the
// beat that is `lead` seconds away, so notes are born out of sight and land
// exactly on a beat. What lands there depends on the level: steady quarter
// notes with the odd rest at first, then holds, off-beat eighths, chords and
// sixteenth pickups as the song goes on.

export interface ChartNote {
  // Game time the note should be hit.
  at: number;
  chord: boolean;
  // Hold length in seconds, or 0.
  hold: number;
  // Strong beat of the bar (beat one).
  downbeat: boolean;
}

interface LevelShape {
  rest: number;
  eighth: number;
  sixteenth: number;
  chord: number;
  hold: number;
}

// Chances per beat at each level. Level 1 is a warm up anyone can clear.
const SHAPES: LevelShape[] = [
  { rest: 0.3, eighth: 0, sixteenth: 0, chord: 0, hold: 0 },
  { rest: 0.12, eighth: 0.15, sixteenth: 0, chord: 0.05, hold: 0.14 },
  { rest: 0.08, eighth: 0.32, sixteenth: 0, chord: 0.14, hold: 0.14 },
  { rest: 0.05, eighth: 0.45, sixteenth: 0.1, chord: 0.2, hold: 0.12 },
  { rest: 0.03, eighth: 0.55, sixteenth: 0.18, chord: 0.25, hold: 0.12 },
];

// Closest two notes may ever sit, whatever the tempo.
export const MIN_NOTE_GAP = 0.15;

export function createRhythmChart(rng: () => number, difficulty: number) {
  let beatInBar = 0;
  let restedLast = false;
  let lastAt = -Infinity;
  const recent: number[] = [];

  const shapeFor = (level: number) => SHAPES[Math.min(SHAPES.length - 1, Math.max(0, level - 1))];

  const push = (out: ChartNote[], note: ChartNote) => {
    if (note.at - lastAt < MIN_NOTE_GAP) return;
    lastAt = note.at;
    out.push(note);
    recent.push(note.at);
  };

  return {
    // Call on a grid tick. `tickAt` is the game time of the tick, `period` the
    // beat length in seconds, `lead` how long notes are on screen before they
    // land. `energy` (0..1) lets loud passages carry more notes.
    onBeat(tickAt: number, period: number, lead: number, level: number, energy: number, cue?: MusicCue): ChartNote[] {
      const out: ChartNote[] = [];
      const ahead = Math.max(1, Math.ceil(lead / period - 1e-6));
      const at = tickAt + ahead * period;
      const shape = shapeFor(level);
      const scale = Math.max(0.6, Math.min(1.4, difficulty));
      const downbeat = beatInBar === 0;
      beatInBar = (beatInBar + 1) % 4;

      // Never rest on the downbeat, never rest twice in a row.
      const restChance = cue?.quiet ? 0.9 : cue && cue.accent > 0.4 ? 0 : shape.rest / scale;
      const rest = !downbeat && !restedLast && rng() < restChance;
      restedLast = rest;
      if (!rest) {
        const hold = !cue?.quiet && rng() < shape.hold * scale ? period * (1 + Math.floor(rng() * 2)) : 0;
        push(out, { at, chord: !cue?.quiet && rng() < shape.chord * scale * (cue && cue.accent > 0.4 ? 2 : downbeat ? 1.6 : 0.7), hold, downbeat });
        // Holds keep the hand busy; nothing else lands inside one.
        if (hold > 0) return out;
      }
      // Subdivisions only when the beat is long enough to fit them.
      const busy = cue?.quiet ? 0 : 0.7 + (cue?.energy ?? energy) * 0.6;
      if (period / 2 >= MIN_NOTE_GAP && rng() < shape.eighth * scale * busy) {
        push(out, { at: at + period / 2, chord: false, hold: 0, downbeat: false });
      }
      if (period / 4 >= MIN_NOTE_GAP && rng() < shape.sixteenth * scale * busy) {
        push(out, { at: at + (period * 3) / 4, chord: false, hold: 0, downbeat: false });
      }
      return out;
    },

    // Notes per second over the last few seconds of chart: the density the
    // player is facing, for the playtest intensity figure.
    density(now: number, window = 8) {
      while (recent.length && recent[0] < now - window) recent.shift();
      const inside = recent.filter((at) => at <= now + 3).length;
      return inside / window;
    },
  };
}

// How long notes stay on screen: shorter each level, so they come in faster.
export function leadFor(base: number, level: number, difficulty: number, floor: number) {
  return Math.max(floor, (base - (level - 1) * 0.13) / Math.max(0.6, difficulty));
}

// Meter lost on a miss and gained on a hit. Early levels forgive, later ones bite.
export function missCost(level: number) {
  return Math.min(0.2, 0.08 + (level - 1) * 0.025);
}

export function hitGain(perfect: boolean, level: number) {
  return (perfect ? 0.025 : 0.012) * (level >= 4 ? 0.7 : 1);
}
